import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppDispatch, AppState } from '../store';
import {
  fetchAllBlogs,
  fetchBlogBySlug,
  fetchFeaturedBlogs,
  fetchBlogsByCategory,
  incrementBlogViews,
  fetchBlogComments,
  addBlogComment as addBlogCommentService,
  fetchBlogLikes,
  addBlogLike as addBlogLikeService,
  removeBlogLike as removeBlogLikeService,
  checkUserLike,
  createBlogPost as createBlogPostService,
} from '../../services/firestore/blogService';
import { BlogPost, BlogComment, BlogLike } from '@e-learning/shared/src/store/types';
import {
  setBlogs,
  setSelectedBlog,
  setFeaturedBlogs,
  setLoading,
  setError,
  setBlogComments,
  addBlogComment,
  setBlogLikes,
  addBlogLike,
  removeBlogLike,
  incrementBlogViews as incrementBlogViewsAction,
} from '@e-learning/shared/src/store/slices/blogsSlice';

/**
 * Fetch all published blogs
 */
export const fetchBlogs = createAsyncThunk<
  BlogPost[],
  void,
  { dispatch: AppDispatch; state: AppState }
>('blogs/fetchBlogs', async (_, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const blogs = await fetchAllBlogs();
    dispatch(setBlogs(blogs));
    dispatch(setLoading(false));
    return blogs;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch blogs';
    dispatch(setError(errorMessage));
    throw error;
  }
});

/**
 * Fetch a blog by slug
 */
export const fetchBlog = createAsyncThunk<
  BlogPost | null,
  string,
  { dispatch: AppDispatch; state: AppState }
>('blogs/fetchBlog', async (slug, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const blog = await fetchBlogBySlug(slug);
    if (blog) {
      dispatch(setSelectedBlog(blog));
      // Increment views
      await incrementBlogViews(blog.id);
      dispatch(incrementBlogViewsAction(blog.id));
      // Fetch comments and likes
      dispatch(fetchBlogCommentsThunk(blog.id));
      dispatch(fetchBlogLikesThunk(blog.id));
    }
    dispatch(setLoading(false));
    return blog;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch blog';
    dispatch(setError(errorMessage));
    throw error;
  }
});

/**
 * Fetch featured blogs
 */
export const fetchFeaturedBlogsThunk = createAsyncThunk<
  BlogPost[],
  number | undefined,
  { dispatch: AppDispatch; state: AppState }
>('blogs/fetchFeaturedBlogs', async (count = 3, { dispatch }) => {
  try {
    const blogs = await fetchFeaturedBlogs(count);
    dispatch(setFeaturedBlogs(blogs));
    return blogs;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch featured blogs';
    dispatch(setError(errorMessage));
    throw error;
  }
});

/**
 * Fetch blogs by category
 */
export const fetchBlogsByCategoryThunk = createAsyncThunk<
  BlogPost[],
  string,
  { dispatch: AppDispatch; state: AppState }
>('blogs/fetchBlogsByCategory', async (category, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const blogs = await fetchBlogsByCategory(category);
    dispatch(setBlogs(blogs));
    dispatch(setLoading(false));
    return blogs;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch blogs by category';
    dispatch(setError(errorMessage));
    throw error;
  }
});

/**
 * Fetch comments for a blog
 */
export const fetchBlogCommentsThunk = createAsyncThunk<
  BlogComment[],
  string,
  { dispatch: AppDispatch; state: AppState }
>('blogs/fetchComments', async (blogId, { dispatch }) => {
  try {
    const comments = await fetchBlogComments(blogId);
    dispatch(setBlogComments({ blogId, comments }));
    return comments;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch comments';
    dispatch(setError(errorMessage));
    throw error;
  }
});

/**
 * Add a comment to a blog
 */
export const addBlogCommentThunk = createAsyncThunk<
  BlogComment,
  { blogId: string; userId: string; userEmail: string | null; userName: string | null; comment: string },
  { dispatch: AppDispatch; state: AppState }
>('blogs/addComment', async ({ blogId, userId, userEmail, userName, comment }, { dispatch }) => {
  try {
    const newComment = await addBlogCommentService(blogId, userId, userEmail, userName, comment);
    dispatch(addBlogComment(newComment));
    return newComment;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to add comment';
    dispatch(setError(errorMessage));
    throw error;
  }
});

/**
 * Fetch likes for a blog
 */
export const fetchBlogLikesThunk = createAsyncThunk<
  BlogLike[],
  string,
  { dispatch: AppDispatch; state: AppState }
>('blogs/fetchLikes', async (blogId, { dispatch, getState }) => {
  try {
    const likes = await fetchBlogLikes(blogId);
    dispatch(setBlogLikes({ blogId, likes }));
    
    // Check if current user has liked
    const state = getState();
    const userId = state.auth.user?.uid;
    if (userId) {
      const userLiked = likes.some((like) => like.userId === userId);
      if (userLiked) {
        dispatch(addBlogLike({ id: '', blogId, userId, createdAt: '' }));
      }
    }
    
    return likes;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch likes';
    dispatch(setError(errorMessage));
    throw error;
  }
});

/**
 * Toggle like on a blog
 */
export const toggleBlogLikeThunk = createAsyncThunk<
  void,
  { blogId: string; userId: string },
  { dispatch: AppDispatch; state: AppState }
>('blogs/toggleLike', async ({ blogId, userId }, { dispatch, getState }) => {
  try {
    const state = getState();
    const isLiked = state.blogs.userLikes[blogId] || false;
    
    if (isLiked) {
      await removeBlogLikeService(blogId, userId);
      dispatch(removeBlogLike({ blogId, userId }));
    } else {
      const like = await addBlogLikeService(blogId, userId);
      dispatch(addBlogLike(like));
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to toggle like';
    dispatch(setError(errorMessage));
    throw error;
  }
});

/**
 * Create a new blog post (requires admin approval)
 */
export const createBlogPostThunk = createAsyncThunk<
  string,
  {
    title: string;
    slug: string;
    content: string;
    excerpt: string;
    authorId: string;
    authorName: string;
    authorEmail: string;
    category: string;
    tags: string[];
    featuredImage?: string;
  },
  { dispatch: AppDispatch; state: AppState }
>('blogs/createBlogPost', async (blogData, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const blogId = await createBlogPostService(
      blogData.title,
      blogData.slug,
      blogData.content,
      blogData.excerpt,
      blogData.authorId,
      blogData.authorName,
      blogData.authorEmail,
      blogData.category,
      blogData.tags,
      blogData.featuredImage
    );
    dispatch(setLoading(false));
    return blogId;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to create blog post';
    dispatch(setError(errorMessage));
    dispatch(setLoading(false));
    throw error;
  }
});

